import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useStore } from '@nanostores/react';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';

export const GlobalCSSVariable = {
  MainNavWidth: '--hydra-main-nav-width',
  MainNavTransitionTime: '--hydra-main-nav-transition-time', // set in index.scss, not usually set dynamically
  MainNavTransitionCurve: '--hydra-main-nav-transition-curve', // set in index.scss, not usually set dynamically
  TopNavHeight: '--hydra-top-nav-height',
  MasqBannerHeight: '--hydra-masq-banner-height',
  AssistantFooterHeight: '--hydra-assistant-footer-height',
  WorkspaceBorderHeight: '--hydra-workspace-border-height',
  WorkspaceBorderColor: '--hydra-workspace-border-color',
  WorkspaceDeliveryModeBannerHeight: '--hydra-workspace-delivery-mode-banner-height',
} as const;

type GlobalCSSVariable = (typeof GlobalCSSVariable)[keyof typeof GlobalCSSVariable];

interface IThemeContext {
  colorScheme: ColorSchemePreference;
  setColorScheme: (colorScheme: ColorSchemePreference) => void;
  setVariable: (variable: GlobalCSSVariable, value: string) => void;
  setVariables: (variables: Partial<Record<GlobalCSSVariable, string>>) => void;
  getVariable: (variable: GlobalCSSVariable) => string;
  removeVariable: (variable: GlobalCSSVariable) => void;
}

const ThemeContext = createContext<IThemeContext | null>(null);

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (context == null) {
    throw new Error('useThemeContext must be used within a ThemeContext');
  }
  return context;
}

const COLOR_SCHEME_KEY = 'color-scheme';
const COLOR_SCHEME_EXPLICIT_KEY = 'color-scheme-explicit';

// One-time migration from the previous boolean-based keys
// (`dark-mode-enabled` / `dark-mode-explicit`) to the new
// `color-scheme` / `color-scheme-explicit` keys. Runs at module load
// before `useLocalStorage` reads, so existing user preferences carry
// across the rename without resetting to light.
(function migrateLegacyDarkModeKeys() {
  try {
    if (localStorage.getItem(COLOR_SCHEME_KEY) != null) return;

    const legacyValue = localStorage.getItem('dark-mode-enabled');
    if (legacyValue == null) return;

    const wasDark = JSON.parse(legacyValue) === true;
    localStorage.setItem(COLOR_SCHEME_KEY, JSON.stringify(wasDark ? 'dark' : 'light'));

    if (localStorage.getItem('dark-mode-explicit') != null) {
      localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
    }

    localStorage.removeItem('dark-mode-enabled');
    localStorage.removeItem('dark-mode-explicit');
  } catch {
    // localStorage may be unavailable (private mode, blocked) — skip migration
  }
})();

export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const isDarkModeToggleFlagEnabled = useFeatureFlag(FeatureFlag.DarkModeToggle);
  const auth = useStore(authStore);
  const isDarkModeToggleEnabled = isDarkModeToggleFlagEnabled || isBuilderPlan(auth.account);
  const [colorScheme, setColorScheme] = useLocalStorage<ColorSchemePreference>(COLOR_SCHEME_KEY, 'light');
  const hasAppliedDefault = useRef(false);

  // When auth loads with a builder plan and the user has never explicitly
  // chosen a color scheme, default to dark. We use a separate localStorage
  // key to distinguish "user chose light" from "never set a preference".
  useEffect(() => {
    if (hasAppliedDefault.current) return;
    if (auth.account == null) return;

    const hasExplicitPreference = localStorage.getItem(COLOR_SCHEME_EXPLICIT_KEY) != null;
    if (!hasExplicitPreference && isBuilderPlan(auth.account)) {
      setColorScheme('dark');
    }
    hasAppliedDefault.current = true;
  }, [auth.account, setColorScheme]);

  const setColorSchemeExplicitly = (value: ColorSchemePreference) => {
    localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
    setColorScheme(value);
  };

  const setVariable = (variable: GlobalCSSVariable, value: string) => {
    document.documentElement.style.setProperty(variable, value);
  };

  const setVariables = (variables: Partial<Record<GlobalCSSVariable, string>>) => {
    for (const [variable, value] of Object.entries(variables)) {
      setVariable(variable as GlobalCSSVariable, value);
    }
  };

  const getVariable = (variable: GlobalCSSVariable) => {
    return document.documentElement.style.getPropertyValue(variable);
  };

  const removeVariable = (variable: GlobalCSSVariable) => {
    document.documentElement.style.removeProperty(variable);
  };

  return (
    <ThemeContext.Provider
      value={{
        colorScheme: isDarkModeToggleEnabled ? colorScheme : 'light',
        setColorScheme: setColorSchemeExplicitly,
        setVariable,
        setVariables,
        getVariable,
        removeVariable,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
